/* MySchoolRx — Contact page. Submits via /api/contact (stored + emailed to the
   approved recipient through the existing email integration). */
import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Compass, LifeBuoy, MessageSquare, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PublicPageShell, BLUE, BLUE_HOVER } from "./publicChrome";

const API = process.env.REACT_APP_BACKEND_URL;

const TOPICS = [
  "Founding Partner program",
  "School or Portfolio demonstration",
  "Sign-in or account access",
  "Product feedback",
  "Privacy inquiry",
  "Other",
];

const HELP_ITEMS = [
  { icon: Compass, title: "Explore MySchoolRx", body: "Ask about MySchoolRx, Portfolio use, or becoming a Founding Partner." },
  { icon: LifeBuoy, title: "Get account support", body: "Request help with sign-in, invitations, account setup, or access to your school or Portfolio." },
  { icon: MessageSquare, title: "Share product feedback", body: "Tell us what is working, what is confusing, and what would make MySchoolRx more useful." },
  { icon: ShieldCheck, title: "Ask about privacy", body: "Contact us with questions about information collected through the website or platform." },
];

const Section = ({ children, className = "" }) => (
  <section className={`mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</section>
);

const inputClass =
  "h-11 rounded-lg border-[#e5e7eb] bg-white text-[#111827] text-[16px] placeholder:text-[#9ca3af] focus-visible:ring-2 focus-visible:ring-[#007a9e]/30 focus-visible:border-[#007a9e]";
const labelClass = "text-[15px] font-semibold text-[#374151] mb-1.5 block";

const MySchoolRxContact = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [org, setOrg] = useState("");
  const [topic, setTopic] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = firstName.trim() && lastName.trim() && email.trim() && topic && message.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || loading) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/api/contact`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        work_email: email.trim(),
        organization: org.trim(),
        topic,
        message: message.trim(),
      });
      setSent(true);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "We couldn't send your message. Please check your details and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicPageShell
      title="Contact MySchoolRx | 589 Labs LLC"
      description="Questions about MySchoolRx, your account, or the Founding Partner program? Contact the MySchoolRx team, operated by 589 Labs LLC."
      canonicalPath="/contact"
      testid="contact-page"
    >
      <div className="border-b border-[#e5e7eb] bg-[#f9fbfc]">
        <Section className="py-14 sm:py-18">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#111827] tracking-tight leading-[1.08]" data-testid="contact-title">
            Contact MySchoolRx
          </h1>
          <p className="mt-5 text-[20px] sm:text-[22px] text-[#4b5563] leading-relaxed max-w-2xl">
            Questions about the Diagnostic, your account, or the Founding Partner program? Get in touch.
          </p>
        </Section>
      </div>

      <Section className="py-12 sm:py-16">
        <h2 className="text-[26px] sm:text-[30px] font-bold text-[#111827] tracking-tight">How can we help?</h2>
        <div className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="contact-help-grid">
          {HELP_ITEMS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="flex items-start gap-4 rounded-xl border border-[#e5e7eb] bg-white p-5">
              <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: "#e6f4f8" }}>
                <Icon className="h-5 w-5" style={{ color: BLUE }} />
              </div>
              <div>
                <div className="text-[17px] font-semibold text-[#111827]">{title}</div>
                <p className="mt-1 text-[15px] text-[#4b5563] leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Contact form */}
        <div className="mt-12 rounded-2xl border border-[#e5e7eb] bg-white p-6 sm:p-8" data-testid="contact-form-card">
          <h2 className="text-[22px] font-bold text-[#111827]">Send a message</h2>
          <p className="mt-2 text-[15px] text-[#b45309] bg-[#fffbeb] border border-[#fde68a] rounded-lg px-4 py-3" data-testid="contact-helper-text">
            Please do not include passwords, student names, student records, or other sensitive information in this form.
          </p>

          {sent ? (
            <div className="mt-6 flex items-start gap-3 rounded-xl border border-[#a7f3d0] bg-[#ecfdf5] p-5" data-testid="contact-success">
              <CheckCircle2 className="h-6 w-6 text-[#059669] shrink-0" />
              <div>
                <div className="text-[18px] font-semibold text-[#065f46]">Thank you. Your message has been received.</div>
                <p className="mt-1 text-[15px] text-[#047857]">We&apos;ll review your message and follow up at the email you provided.</p>
              </div>
            </div>
          ) : (
            <form className="mt-6 space-y-5" onSubmit={submit} data-testid="contact-form" noValidate>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="contact-first-name" className={labelClass}>First name</Label>
                  <Input id="contact-first-name" name="firstName" autoComplete="given-name" value={firstName}
                    onChange={(e) => setFirstName(e.target.value)} className={inputClass} data-testid="contact-first-name" />
                </div>
                <div>
                  <Label htmlFor="contact-last-name" className={labelClass}>Last name</Label>
                  <Input id="contact-last-name" name="lastName" autoComplete="family-name" value={lastName}
                    onChange={(e) => setLastName(e.target.value)} className={inputClass} data-testid="contact-last-name" />
                </div>
              </div>
              <div>
                <Label htmlFor="contact-work-email" className={labelClass}>Work email</Label>
                <Input id="contact-work-email" name="workEmail" type="email" autoComplete="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} className={inputClass} data-testid="contact-work-email" />
              </div>
              <div>
                <Label htmlFor="contact-organization" className={labelClass}>School or organization</Label>
                <Input id="contact-organization" name="organization" autoComplete="organization" value={org}
                  onChange={(e) => setOrg(e.target.value)} className={inputClass} data-testid="contact-organization" />
              </div>
              <div>
                <Label htmlFor="contact-topic" className={labelClass}>Topic</Label>
                <Select value={topic} onValueChange={setTopic}>
                  <SelectTrigger id="contact-topic" className={inputClass} data-testid="contact-topic">
                    <SelectValue placeholder="Select a topic" />
                  </SelectTrigger>
                  <SelectContent>
                    {TOPICS.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`contact-topic-${t.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="contact-message" className={labelClass}>Message</Label>
                <Textarea id="contact-message" name="message" value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="min-h-[150px] rounded-lg border-[#e5e7eb] bg-white text-[#111827] text-[16px] placeholder:text-[#9ca3af] focus-visible:ring-2 focus-visible:ring-[#007a9e]/30 focus-visible:border-[#007a9e] resize-y"
                  data-testid="contact-message" />
              </div>

              {error && (
                <p className="text-[15px] text-[#b91c1c] bg-[#fef2f2] border border-[#fecaca] rounded-lg px-4 py-3" role="alert" data-testid="contact-error">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                disabled={!canSubmit || loading}
                className="h-12 text-[17px] text-white px-6 w-full sm:w-auto disabled:opacity-50"
                style={{ backgroundColor: BLUE }}
                onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = BLUE_HOVER; }}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BLUE)}
                data-testid="contact-submit"
              >
                {loading ? "Sending…" : "Send message"} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </form>
          )}
        </div>

        {/* Sign-in help */}
        <div className="mt-10 rounded-xl border border-[#e5e7eb] bg-[#f9fbfc] p-6" data-testid="contact-signin-help">
          <h3 className="text-[18px] font-bold text-[#111827]">Need help signing in?</h3>
          <p className="mt-2 text-[16px] text-[#4b5563] leading-relaxed">
            Use the existing{" "}
            <a href="/diagnostic/login" className="font-semibold hover:underline" style={{ color: BLUE }} data-testid="contact-signin-link">Sign in</a>{" "}
            and{" "}
            <a href="/diagnostic/login" className="font-semibold hover:underline" style={{ color: BLUE }} data-testid="contact-forgot-link">Forgot Password</a>{" "}
            links. If you still cannot access your account, select &ldquo;Sign-in or account access&rdquo; above and describe the
            issue without sharing your password.
          </p>
        </div>

        <p className="mt-10 text-[15px] text-[#6b7280]" data-testid="contact-operator-line">
          MySchoolRx is operated by 589 Labs LLC.
        </p>
      </Section>
    </PublicPageShell>
  );
};

export default MySchoolRxContact;
